import { toKebabCase } from '@/utils/case';
import {
  arrayRows,
  asDynamicFieldType,
  groupValue,
  isDynamicValueEmpty,
  isNestedFieldType,
  newRowId,
  ROW_ID_KEY,
} from '@/utils/dynamic-fields';
import { isPlainObject } from '@/utils/isPlainObject';

import type { DynamicFieldDefinition, ScalarFieldType } from '@/utils/dynamic-fields';

const TRUTHY = new Set(['1', 'true', 'yes', 'y', 'on']);

const matchOption = (options: DynamicFieldDefinition['options'], raw: unknown): string | null => {
  const value = String(raw).trim();
  if (!options?.length) return value;
  const exact = options.find((option) => option.value === value);
  if (exact) return exact.value;
  const byLabel = options.find((option) => option.label.toLowerCase() === value.toLowerCase());
  return byLabel ? byLabel.value : null;
};

const ISO_DATE = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])/;
const DAY_FIRST_DATE = /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/(\d{4})$/;

/**
 * A date input needs `YYYY-MM-DD`, and a source may not oblige: Qi returns ISO on some
 * fields (sometimes with a time) and day-first `DD/MM/YYYY` on others. The slashed form
 * is read as day-first, the convention where these sources are authored; anything that
 * matches neither is skipped rather than guessed, so a date field is never filled with
 * a value that only looks like a date.
 */
const toIsoDate = (raw: unknown): string | null => {
  const value = String(raw).trim();

  const iso = ISO_DATE.exec(value);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dayFirst = DAY_FIRST_DATE.exec(value);
  if (dayFirst) return `${dayFirst[3]}-${dayFirst[2]}-${dayFirst[1]}`;

  return null;
};

type Coercion = (raw: unknown, definition: DynamicFieldDefinition) => unknown;

const COERCIONS: Record<ScalarFieldType, Coercion> = {
  'text-field': (raw) => String(raw),
  'textarea-field': (raw) => String(raw),
  'number-field': (raw) => {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  },
  'checkbox-field': (raw) => TRUTHY.has(String(raw).trim().toLowerCase()),
  'date-field': toIsoDate,
  'select-field': (raw, definition) => matchOption(definition.options, raw),
};

/**
 * External references carry slashes, spaces and mixed case (e.g. `2001/00123 A`),
 * none of which belong in a slug that keys a public URL.
 */
export const referenceToSlug = (reference: string): string => toKebabCase(reference.replace(/\//g, ' '));

const isBlank = (value: unknown): boolean => value == null || String(value).trim() === '';

/** Whether an import may replace values that already have content. `slug` is never replaced. */
export interface ImportMode {
  replaceExisting?: boolean;
}

/**
 * Picks the core fields an import should write. By default only fills what is still empty, so a
 * re-import never overwrites an edit the curator made by hand; `replaceExisting` lifts that for
 * `title` and `description` only.
 *
 * `slug` is deliberately excluded from `replaceExisting`: it keys a public URL, so replacing a
 * corrected slug would move a published page.
 */
export const mapRecordToCoreFields = (
  source: { title?: string; reference?: string; description?: string },
  current: { title?: unknown; slug?: unknown; description?: unknown } = {},
  { replaceExisting = false }: ImportMode = {},
): { title?: string; slug?: string; description?: string } => ({
  ...(source.title && (replaceExisting || isBlank(current.title)) && { title: source.title }),
  ...(source.reference && isBlank(current.slug) && { slug: referenceToSlug(source.reference) }),
  ...(source.description && (replaceExisting || isBlank(current.description)) && { description: source.description }),
});

/**
 * Fills a Collection Config's dynamic fields from a normalised source record by
 * matching each definition's `name` against the record's keys. Adapters flatten
 * their own nesting first, so this stays source-agnostic.
 *
 * By default only fills fields that are currently empty, so a re-import refreshes what the curator
 * has not filled in and never overwrites an edit they made by hand. `replaceExisting` lifts that —
 * but a field the source has no value for is still skipped, so an import can never blank one.
 */
/** The scalar fields of one level, applying the fill-only-what-is-empty rule per field. */
const mapScalars = (
  definitions: DynamicFieldDefinition[],
  record: Record<string, unknown>,
  current: Record<string, unknown>,
  replaceExisting: boolean,
): Record<string, unknown> => {
  const mapped: Record<string, unknown> = {};

  for (const definition of definitions) {
    if (!definition?.name || isNestedFieldType(definition.blockType)) continue;
    if (!replaceExisting && !isDynamicValueEmpty(definition, current[definition.name])) continue;
    const raw = record[definition.name];
    if (raw == null || raw === '') continue;
    // A scalar field cannot hold a source object or array. Coercing one would store "[object
    // Object]", which reads as real content on a page.
    if (typeof raw === 'object') continue;
    const value = COERCIONS[asDynamicFieldType(definition.blockType) as ScalarFieldType](raw, definition);
    if (value !== null) mapped[definition.name] = value;
  }

  return mapped;
};

const mapGroup = (
  definition: DynamicFieldDefinition,
  record: Record<string, unknown>,
  current: Record<string, unknown>,
  replaceExisting: boolean,
): unknown => {
  const source = record[definition.name];
  if (!isPlainObject(source)) return;

  const existing = groupValue(current[definition.name]);
  const filled = mapRecordToDynamicFields(definition.fields ?? [], source, existing, { replaceExisting });
  if (Object.keys(filled).length === 0) return;

  return { ...existing, ...filled };
};

/**
 * Fills an array field only when it holds no rows. With rows already there, no rule says which
 * source row matches which existing one, and guessing would overwrite what a curator wrote. This is
 * the same fill-only-what-is-empty rule the scalar fields follow, applied to the whole field.
 *
 * `replaceExisting` lifts that, and there it has to guess: a source row is matched to the stored row
 * in the same position, the only ordering both sides share. A source row that maps to nothing takes no
 * position, which is what keeps a re-import idempotent. The match carries the stored row's `id`
 * — so the orphan guard sees one row, not a delete and an insert — and any child the source has no
 * value for, following the same rule the scalar fields do. Rows past the source's length are
 * dropped: the source owns the list.
 */
const mapArray = (
  definition: DynamicFieldDefinition,
  record: Record<string, unknown>,
  current: Record<string, unknown>,
  replaceExisting: boolean,
): unknown => {
  if (!replaceExisting && !isDynamicValueEmpty(definition, current[definition.name])) return;

  const source = record[definition.name];
  if (!Array.isArray(source)) return;

  const existing = arrayRows(current[definition.name]);

  const rows: Record<string, unknown>[] = [];
  for (const row of source.filter(isPlainObject)) {
    const previous = existing[rows.length] ?? {};
    const filled = mapRecordToDynamicFields(definition.fields ?? [], row, previous, { replaceExisting });
    if (Object.keys(filled).length === 0) continue;
    const id = previous[ROW_ID_KEY];

    rows.push({ ...previous, ...filled, [ROW_ID_KEY]: typeof id === 'string' && id ? id : newRowId() });
  }

  if (rows.length === 0) return;

  return rows;
};

export const mapRecordToDynamicFields = (
  definitions: DynamicFieldDefinition[],
  record: Record<string, unknown>,
  current: Record<string, unknown> = {},
  { replaceExisting = false }: ImportMode = {},
): Record<string, unknown> => {
  const mapped = mapScalars(definitions, record, current, replaceExisting);

  for (const definition of definitions) {
    if (!definition?.name || !isNestedFieldType(definition.blockType)) continue;

    const value =
      definition.blockType === 'group-field'
        ? mapGroup(definition, record, current, replaceExisting)
        : mapArray(definition, record, current, replaceExisting);

    if (value !== undefined) mapped[definition.name] = value;
  }

  return mapped;
};
